/**
 * DrugIdentificationList — 낱알 식별 의약품 목록
 * 모바일 앱에서 촬영·식별된 약물 데이터를 독립 섹션으로 표시한다.
 * 처방전/OTC/건기식으로 분류하지 않고, 약품별 메모 기능을 제공한다.
 */
import React, { useState, useRef, useEffect, useCallback } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Text from "components/atoms/Text";
import Icon from "components/atoms/Icon";
import Tag from "components/atoms/Tag";
import Button from "components/atoms/Button";
import { DrugIdentification } from "common/models/types";
import {
  DRUG_IDENTIFICATION_IMAGE_BASE_URL,
  deleteDrugIdentification,
  updateDrugIdentificationPatient,
} from "api/drugIdentificationService";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import { SlideManager } from "components/organisms/CommonUI/SlidePopup/SlideManager";
import DrugIdentificationDetailPopup from "pages/Admin/DrugIdentifications/DrugIdentificationDetailPopup";

/** S3 촬영 사진 URL 목록 생성 (1-indexed, 최대 3장 시도 — onError로 미존재 이미지 숨김) */
function getDrugIdImageUrls(id: number): string[] {
  return [1, 2, 3].map(i => `${DRUG_IDENTIFICATION_IMAGE_BASE_URL}/${id}-${i}.jpg`);
}

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ItemCard = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 8px;
  align-items: flex-start;
  cursor: pointer;

  &:hover {
    border-color: ${COLOR_SYSTEM.get("Gray")?.[200]};
    background: ${COLOR_SYSTEM.get("Gray")?.[10]};
  }
`;

const ThumbnailGroup = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

const Thumbnail = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 6px;
  object-fit: cover;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`;

const ThumbnailPlaceholder = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 6px;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const ItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
`;

const MemoArea = styled.div`
  margin-top: 8px;
`;

const MemoInput = styled.textarea`
  width: 100%;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  font-family: inherit;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  background: ${COLOR_SYSTEM.get("Gray")?.[10]};
  resize: none;
  outline: none;
  min-height: 20px;
  line-height: 1.4;
  overflow: hidden;

  &:focus {
    border-color: ${COLOR_SYSTEM.get("Blue")?.[300]};
    background: #fff;
  }

  &::placeholder {
    color: ${COLOR_SYSTEM.get("Gray")?.[300]};
  }
`;

const MemoDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  line-height: 1.4;
  cursor: pointer;
  padding: 4px 0;

  &:hover {
    color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  }
`;

const MemoPlaceholder = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[300]};
  cursor: pointer;
  padding: 4px 0;

  &:hover {
    color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  }
`;

const DeleteButtonWrapper = styled.div`
  flex-shrink: 0;
  align-self: center;
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  font-size: 14px;
`;

const ImageOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  cursor: pointer;
`;

const EnlargedImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 8px;
  object-fit: contain;
`;

function formatDate(d?: Date | string): string {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d;
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getDate()).padStart(2, "0")}`;
}

/** 메모 인라인 편집 컴포넌트 */
const InlineMemo: React.FC<{ itemId: number; initial?: string }> = ({ itemId, initial = "" }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initial);
  const ref = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(() => {
    if (ref.current) {
      ref.current.style.height = "auto";
      ref.current.style.height = ref.current.scrollHeight + "px";
    }
  }, []);

  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      adjustHeight();
    }
  }, [editing, adjustHeight]);

  const handleBlur = async () => {
    setEditing(false);
    if (value !== initial) {
      try {
        await updateDrugIdentificationPatient(itemId, { note: value || null });
      } catch {
        ToastManager.show({ content: "메모 저장에 실패했습니다.", duration: 3000 });
        setValue(initial);
      }
    }
  };

  if (!editing) {
    if (value) {
      return (
        <MemoDisplay onClick={() => setEditing(true)}>
          <Icon name="Edit 01" size={12} color={COLOR_SYSTEM.get("Gray")?.[400]} style={{ flexShrink: 0 }} />
          {value}
        </MemoDisplay>
      );
    }
    return (
      <MemoPlaceholder onClick={() => setEditing(true)}>
        <Icon name="Edit 01" size={12} color={COLOR_SYSTEM.get("Gray")?.[300]} style={{ flexShrink: 0 }} />
        메모 입력
      </MemoPlaceholder>
    );
  }

  return (
    <MemoInput
      ref={ref}
      value={value}
      placeholder="메모를 입력하세요"
      onChange={(e) => {
        setValue(e.target.value);
        adjustHeight();
      }}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          handleBlur();
        }
      }}
      rows={1}
    />
  );
};

interface DrugIdentificationListProps {
  items: DrugIdentification[];
  onDelete?: (id: number) => void;
}

/** 개별 식별 아이템 카드 */
const DrugIdentificationItem: React.FC<{
  item: DrugIdentification;
  onEnlarge: (url: string) => void;
  onDelete?: (id: number) => void;
}> = ({ item, onEnlarge, onDelete }) => {
  const imageUrls = item.id ? getDrugIdImageUrls(item.id) : [];
  const selectedDrug =
    item.selectedItemCode
      ? item.results?.find((r) => r.itemSeq === item.selectedItemCode)
      : null;
  const topCandidate = !selectedDrug && item.results?.[0];
  const drugName = selectedDrug
    ? selectedDrug.itemName
    : topCandidate
      ? topCandidate.itemName
      : "미식별";

  const handleOpenDetail = () => {
    if (!item.id) return;
    SlideManager.show({
      body: <DrugIdentificationDetailPopup identificationId={item.id} />,
      direction: "right",
      width: "800px",
      needCloseButton: true,
    });
  };

  const handleDelete = () => {
    if (!item.id) return;
    ConfirmManager.show({
      title: "식별 의약품 삭제",
      body: (
        <div>
          <Text textType="Body1" style={{ marginBottom: "16px" }}>
            {drugName}을(를) 삭제하시겠습니까?
          </Text>
          <Text textType="Body2" color="var(--gray-600)">
            삭제된 식별 의약품은 복구할 수 없습니다.
          </Text>
        </div>
      ),
      buttons: [
        <Button
          key="cancel"
          buttonType="button"
          color="Tertiary"
          size="Medium"
          type="Text"
          variant="Outlined"
          onClick={() => ConfirmManager.hide()}
        >
          취소
        </Button>,
        <Button
          key="delete"
          buttonType="button"
          color="Negative"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={async () => {
            try {
              await deleteDrugIdentification(item.id!);
              ConfirmManager.hide();
              ToastManager.show({ content: "식별 의약품이 삭제되었습니다.", duration: 2000 });
              onDelete?.(item.id!);
            } catch {
              ConfirmManager.hide();
              ToastManager.show({ content: "삭제에 실패했습니다.", duration: 3000 });
            }
          }}
        >
          삭제
        </Button>,
      ],
    });
  };

  return (
    <ItemCard onClick={handleOpenDetail}>
      <ThumbnailGroup onClick={(e) => e.stopPropagation()}>
        {imageUrls.length > 0 ? (
          imageUrls.map((url, idx) => (
            <Thumbnail
              key={idx}
              src={url}
              alt={`식별 사진 ${idx + 1}`}
              onClick={() => onEnlarge(url)}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ))
        ) : (
          <ThumbnailPlaceholder>
            <Icon name="Camera" size={20} color={COLOR_SYSTEM.get("Gray")?.[300]} />
          </ThumbnailPlaceholder>
        )}
      </ThumbnailGroup>

      <ItemInfo>
        <ItemHeader>
          <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[800]} style={{ fontWeight: 600 }}>
            {drugName}
          </Text>
          {selectedDrug && (
            <Tag variant="Tinted" color="Green" size="Small">확정</Tag>
          )}
          {!selectedDrug && topCandidate && (
            <Tag variant="Tinted" color="Blue" size="Small">후보 {item.results?.length}건</Tag>
          )}
          {!selectedDrug && !topCandidate && (
            <Tag variant="Tinted" color="Red" size="Small">미식별</Tag>
          )}
        </ItemHeader>

        <ItemMeta>
          <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[400]}>
            {formatDate(item.createdAt)}
          </Text>
        </ItemMeta>

        <MemoArea onClick={(e) => e.stopPropagation()}>
          <InlineMemo itemId={item.id!} initial={item.note ?? ""} />
        </MemoArea>
      </ItemInfo>

      {onDelete && (
        <DeleteButtonWrapper onClick={(e) => e.stopPropagation()}>
          <Button
            buttonType="button"
            color="Negative"
            size="Small"
            type="Text"
            variant="Outlined"
            onClick={handleDelete}
          >
            삭제
          </Button>
        </DeleteButtonWrapper>
      )}
    </ItemCard>
  );
};

const DrugIdentificationList: React.FC<DrugIdentificationListProps> = ({ items, onDelete }) => {
  const [enlargedUrl, setEnlargedUrl] = useState<string | null>(null);

  if (items.length === 0) {
    return <EmptyState>식별 의약품이 없습니다.</EmptyState>;
  }

  return (
    <>
      <List>
        {items.map((item) => (
          <DrugIdentificationItem
            key={item.id}
            item={item}
            onEnlarge={setEnlargedUrl}
            onDelete={onDelete}
          />
        ))}
      </List>

      {enlargedUrl && (
        <ImageOverlay onClick={() => setEnlargedUrl(null)}>
          <EnlargedImage src={enlargedUrl} alt="확대 이미지" />
        </ImageOverlay>
      )}
    </>
  );
};

export default DrugIdentificationList;
